import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DataService } from '../data.service';

type EditorTab = 'products' | 'categories' | 'coupons';

interface WebshopCategory {
  id?: number | null;
  localId?: string;
  name: string;
  name_en: string;
  leiras: string;
  pic: string;
  parent?: number | null;
  home_visible: number;
  home_background: string;
  sort_order: number;
  active: number;
  count?: number;
}

interface WebshopProduct {
  id?: number | null;
  name: string;
  name_en: string;
  cikkszam: string;
  normal_price: number;
  discount_price: number;
  is_vat: number;
  vat_class: string;
  keszleten: number;
  db: number;
  vegtelen: number;
  brand: string;
  tags: string;
  pic: string;
  leiras: string;
  leiras_en: string;
  sort_order: number;
  active: number;
  categories: string[];
}

interface WebshopCoupon {
  id?: number | null;
  name: string;
  code: string;
  mode: 'termek' | 'kategoria' | 'global';
  discountmode: 'szazalek' | 'ft';
  data: string[];
  discountdata: number;
  kezdo: string;
  vege: string;
  active: number;
}

@Component({
  selector: 'app-webshop-catalog-admin',
  templateUrl: './webshop-catalog-admin.component.html',
  styleUrls: ['./webshop-catalog-admin.component.scss'],
})
export class WebshopCatalogAdminComponent implements OnInit {
  sajatIntezmeny: any;
  tab: EditorTab = 'products';
  loading = true;
  saving = false;
  error = '';
  search = '';
  categories: WebshopCategory[] = [];
  products: WebshopProduct[] = [];
  coupons: WebshopCoupon[] = [];
  selectedCategoryKey = '';
  selectedProductKey = '';
  selectedCouponKey = '';
  uploadingImageKey = '';

  constructor(
    private http: HttpClient,
    private data: DataService,
  ) {}

  async ngOnInit() {
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    this.loadCatalog();
  }

  get tenantCode(): string {
    return String(this.sajatIntezmeny?.intezmenyId || this.sajatIntezmeny?.om || 'demo').trim();
  }

  get previewUrl(): string {
    return `/api/webshop/${this.tenantCode}/termekek`;
  }

  get selectedCategory(): WebshopCategory | undefined {
    return this.categories.find((category) => this.categoryKey(category) === this.selectedCategoryKey);
  }

  get selectedProduct(): WebshopProduct | undefined {
    return this.products.find((product) => this.productKey(product) === this.selectedProductKey);
  }

  get selectedCoupon(): WebshopCoupon | undefined {
    return this.coupons.find((coupon) => this.couponKey(coupon) === this.selectedCouponKey);
  }

  get filteredCategories(): WebshopCategory[] {
    const needle = this.normalizedSearch;
    return [...this.categories]
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
      .filter((category) => !needle || this.matches(category.name, category.leiras, category.home_background, String(category.id || ''), needle));
  }

  get filteredProducts(): WebshopProduct[] {
    const needle = this.normalizedSearch;
    return [...this.products]
      .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
      .filter((product) => !needle || this.matches(product.name, product.cikkszam, product.leiras, product.brand, product.tags, needle));
  }

  get filteredCoupons(): WebshopCoupon[] {
    const needle = this.normalizedSearch;
    return [...this.coupons]
      .sort((a, b) => String(a.code || '').localeCompare(String(b.code || '')))
      .filter((coupon) => !needle || this.matches(coupon.name, coupon.code, this.couponModeLabel(coupon.mode), needle));
  }

  get normalizedSearch(): string {
    return this.normalize(this.search);
  }

  loadCatalog() {
    this.loading = true;
    this.error = '';

    this.http.get<any>(`/api/webshop/admin/catalog/${this.tenantCode}`).subscribe({
      next: (response) => {
        this.categories = (Array.isArray(response?.categories) ? response.categories : []).map((category: any, index: number) => ({
          id: category.id || null,
          localId: String(category.id || `category-${Date.now()}-${index}`),
          name: category.name || '',
          name_en: category.name_en || category.name || '',
          leiras: category.leiras || '',
          pic: category.pic || '',
          parent: category.parent || null,
          home_visible: Number(category.home_visible ?? 1),
          home_background: category.home_background || '',
          sort_order: Number(category.sort_order ?? ((index + 1) * 10)),
          active: Number(category.active ?? 1),
          count: Number(category.count || 0),
        }));

        this.products = (Array.isArray(response?.products) ? response.products : []).map((product: any, index: number) => ({
          id: product.id || null,
          name: product.name || '',
          name_en: product.name_en || product.name || '',
          cikkszam: product.cikkszam || '',
          normal_price: Number(product.normal_price || product.price || 0),
          discount_price: Number(product.discount_price || 0),
          is_vat: Number(product.is_vat || 0),
          vat_class: product.vat_class || '',
          keszleten: Number(product.keszleten || 0),
          db: Number(product.db || 1),
          vegtelen: Number(product.vegtelen || 0),
          brand: product.brand || '',
          tags: Array.isArray(product.tags) ? product.tags.join(', ') : (product.tags || ''),
          pic: Array.isArray(product.pic) ? (product.pic[0] || '') : (product.pic || ''),
          leiras: product.leiras || '',
          leiras_en: product.leiras_en || '',
          sort_order: Number(product.sort_order ?? ((index + 1) * 10)),
          active: Number(product.active ?? 1),
          categories: Array.isArray(product.categories)
            ? product.categories.map((category: any) => String(category?.id || category)).filter(Boolean)
            : [],
        }));

        this.selectedCategoryKey = this.categoryKey(this.categories[0]);
        this.selectedProductKey = this.productKey(this.products[0]);
        this.loadCoupons();
      },
      error: () => {
        this.error = 'A webshop katalógus adatai nem tölthetők be.';
        this.loading = false;
      },
    });
  }

  loadCoupons() {
    this.http.get<any>(`/api/webshop/admin/coupons/${this.tenantCode}`).subscribe({
      next: (response) => {
        this.coupons = (Array.isArray(response?.coupons) ? response.coupons : []).map((coupon: any) => ({
          id: coupon.id || null,
          name: coupon.name || '',
          code: coupon.code || '',
          mode: ['termek', 'kategoria', 'global'].includes(coupon.mode) ? coupon.mode : 'termek',
          discountmode: ['szazalek', 'ft'].includes(coupon.discountmode) ? coupon.discountmode : 'szazalek',
          data: Array.isArray(coupon.data) ? coupon.data.map((item: any) => String(item?.id || item)).filter(Boolean) : [],
          discountdata: Number(coupon.discountdata || 0),
          kezdo: coupon.kezdo || new Date().toISOString().slice(0, 10),
          vege: coupon.vege || '',
          active: Number(coupon.active ?? 1),
        }));
        this.selectedCouponKey = this.couponKey(this.coupons[0]);
        this.loading = false;
      },
      error: () => {
        this.error = 'A webshop kuponok adatai nem tölthetők be.';
        this.loading = false;
      },
    });
  }

  saveCurrentTab() {
    if (this.tab === 'coupons') {
      this.saveCoupons();
      return;
    }
    this.saveCatalog();
  }

  saveCatalog() {
    this.saving = true;
    this.normalizeOrders();

    this.http.put(`/api/webshop/admin/catalog/${this.tenantCode}`, {
      categories: this.categories,
      products: this.products,
    }).subscribe({
      next: () => {
        this.saving = false;
        this.data.sendToast('A webshop termékek és kategóriák mentve.');
        this.loadCatalog();
      },
      error: (error) => {
        this.saving = false;
        const detail = this.apiErrorMessage(error);
        this.data.sendToast(`Nem sikerült menteni a webshop katalógust${detail ? `: ${detail}` : '.'}`, 'error');
      },
    });
  }

  saveCoupons() {
    this.saving = true;
    this.coupons.forEach((coupon) => {
      coupon.active = Number(coupon.active ?? 1);
      coupon.data = coupon.mode === 'global' ? [] : (coupon.data || []).filter(Boolean);
    });

    this.http.put(`/api/webshop/admin/coupons/${this.tenantCode}`, {
      coupons: this.coupons,
    }).subscribe({
      next: () => {
        this.saving = false;
        this.data.sendToast('A webshop kuponok mentve.');
        this.loadCoupons();
      },
      error: () => {
        this.saving = false;
        this.data.sendToast('Nem sikerült menteni a webshop kuponokat.', 'error');
      },
    });
  }

  addCategory() {
    const localId = `new-category-${Date.now()}`;
    const category: WebshopCategory = {
      id: null,
      localId,
      name: 'Új kategória',
      name_en: '',
      leiras: '',
      pic: '',
      parent: null,
      home_visible: 1,
      home_background: '',
      sort_order: this.categories.length * 10 + 10,
      active: 1,
      count: 0,
    };
    this.categories.push(category);
    this.selectedCategoryKey = localId;
    this.tab = 'categories';
  }

  deleteCategory(category: WebshopCategory | undefined = this.selectedCategory) {
    if (!category) return;
    if (!window.confirm(`Biztosan törlöd ezt a kategóriát: ${category.name}?`)) return;

    const key = this.categoryKey(category);
    this.categories = this.categories.filter((item) => this.categoryKey(item) !== key);
    this.products.forEach((product) => {
      product.categories = product.categories.filter((categoryId) => categoryId !== key && categoryId !== String(category.id));
    });
    this.selectedCategoryKey = this.categoryKey(this.categories[0]);
  }

  addProduct() {
    const product: WebshopProduct = {
      id: null,
      name: 'Új termék',
      name_en: '',
      cikkszam: '',
      normal_price: 0,
      discount_price: 0,
      is_vat: 0,
      vat_class: '',
      keszleten: 0,
      db: 1,
      vegtelen: 0,
      brand: '',
      tags: '',
      pic: '',
      leiras: '',
      leiras_en: '',
      sort_order: this.products.length * 10 + 10,
      active: 1,
      categories: [],
    };
    this.products.push(product);
    this.selectedProductKey = this.productKey(product);
    this.tab = 'products';
  }

  duplicateProduct(product: WebshopProduct | undefined = this.selectedProduct) {
    if (!product) return;
    const copy: WebshopProduct = {
      ...product,
      id: null,
      name: `${product.name} másolat`,
      sort_order: this.products.length * 10 + 10,
      categories: [...product.categories],
    };
    this.products.push(copy);
    this.selectedProductKey = this.productKey(copy);
  }

  deleteProduct(product: WebshopProduct | undefined = this.selectedProduct) {
    if (!product) return;
    if (!window.confirm(`Biztosan törlöd ezt a terméket: ${product.name}?`)) return;

    const key = this.productKey(product);
    this.products = this.products.filter((item) => this.productKey(item) !== key);
    this.selectedProductKey = this.productKey(this.products[0]);
  }

  addCoupon() {
    const coupon: WebshopCoupon = {
      id: null,
      name: 'Új kupon',
      code: '',
      mode: 'termek',
      discountmode: 'szazalek',
      data: [],
      discountdata: 10,
      kezdo: new Date().toISOString().slice(0, 10),
      vege: '',
      active: 1,
    };
    this.coupons.push(coupon);
    this.selectedCouponKey = this.couponKey(coupon);
    this.tab = 'coupons';
  }

  deleteCoupon(coupon: WebshopCoupon | undefined = this.selectedCoupon) {
    if (!coupon) return;
    if (!window.confirm(`Biztosan törlöd ezt a kupont: ${coupon.name}?`)) return;

    const key = this.couponKey(coupon);
    this.coupons = this.coupons.filter((item) => this.couponKey(item) !== key);
    this.selectedCouponKey = this.couponKey(this.coupons[0]);
  }

  couponModeLabel(mode: string): string {
    if (mode === 'termek') return 'Termék';
    if (mode === 'kategoria') return 'Kategória';
    return 'Globális';
  }

  couponDiscountLabel(coupon: WebshopCoupon): string {
    const value = Number(coupon.discountdata || 0);
    return coupon.discountmode === 'ft'
      ? `${value.toLocaleString('hu-HU')} Ft`
      : `${value}%`;
  }

  uploadCatalogImage(event: Event, target: 'product' | 'category', entity: WebshopProduct | WebshopCategory) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.data.sendToast('Csak kép fájl tölthető fel.', 'error');
      input.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('target', target);
    this.uploadingImageKey = `${target}-${this.categoryKey(entity as WebshopCategory) || this.productKey(entity as WebshopProduct)}`;

    this.http.post<any>(`/api/webshop/admin/catalog-upload/${this.tenantCode}`, formData).subscribe({
      next: (response) => {
        entity.pic = response?.url || response?.path || '';
        this.uploadingImageKey = '';
        input.value = '';
        this.data.sendToast('A kép feltöltve, ne felejtsd el menteni a katalógust.');
      },
      error: () => {
        this.uploadingImageKey = '';
        input.value = '';
        this.data.sendToast('Nem sikerült feltölteni a képet.', 'error');
      },
    });
  }

  imagePreviewUrl(value: string): string {
    const raw = String(value || '').trim();
    if (!raw) return '';
    if (/^(https?:)?\/\//i.test(raw) || raw.startsWith('/')) return raw;
    return `/api/file/${raw}`;
  }

  isUploadingImage(target: 'product' | 'category', entity: WebshopProduct | WebshopCategory): boolean {
    return this.uploadingImageKey === `${target}-${this.categoryKey(entity as WebshopCategory) || this.productKey(entity as WebshopProduct)}`;
  }

  categoryKey(category?: WebshopCategory): string {
    if (!category) return '';
    return String(category.localId || category.id || '');
  }

  productKey(product?: WebshopProduct): string {
    if (!product) return '';
    return String(product.id || this.products.indexOf(product));
  }

  couponKey(coupon?: WebshopCoupon): string {
    if (!coupon) return '';
    return String(coupon.id || this.coupons.indexOf(coupon));
  }

  private normalizeOrders() {
    this.categories.forEach((category, index) => {
      category.sort_order = Number(category.sort_order ?? ((index + 1) * 10));
      category.active = Number(category.active ?? 1);
      category.home_visible = Number(category.home_visible ?? 1);
      category.home_background = String(category.home_background || '').trim();
      category.localId = this.categoryKey(category) || `category-${index}`;
    });
    this.products.forEach((product, index) => {
      product.sort_order = Number(product.sort_order ?? ((index + 1) * 10));
      product.active = Number(product.active ?? 1);
      product.brand = String(product.brand || '').trim();
      product.tags = this.normalizeTagsInput(product.tags);
      product.categories = (product.categories || []).filter(Boolean);
    });
  }

  private apiErrorMessage(error: any): string {
    const value = error?.error?.error?.sqlMessage
      || error?.error?.error?.message
      || error?.error?.message
      || error?.message
      || '';

    if (!value) return '';
    if (typeof value === 'string') return value;

    try {
      return JSON.stringify(value);
    } catch (_) {
      return String(value);
    }
  }

  private normalizeTagsInput(value: string): string {
    return String(value || '')
      .split(/[,\n;]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .join(', ');
  }

  private matches(...values: string[]): boolean {
    const needle = values.pop() || '';
    return values.some((value) => this.normalize(value).includes(needle));
  }

  private normalize(value: string): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
